import { doNothing } from "@greeter/util";
import { IonIcon, useIonToast } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { classNames, yesterday } from "@greeter-venue/utility";
import { DailyOpeningHours, WeeklyOpeningHours } from "@greeter/core";
import { Day, TimeOfDay } from "@greeter/date";

import css from "./OpeningHoursInput.module.scss";
import TimeInput from "../TimeInput";

// TODO: Move to GreeterMatter
type Sunday = 0;
type Monday = 1;
type Tuesday = 2;
type Wednesday = 3;
type Thursday = 4;
type Friday = 5;
type Saturday = 6;
type WeekDay =
  | Sunday
  | Monday
  | Tuesday
  | Wednesday
  | Thursday
  | Friday
  | Saturday;

// export type TimeInputProps = {
//   value?: string | undefined | null;
//   onChange?: (val: string) => void;
//   disabled?: boolean;
//   className?: string;
//   placeholder?: string;
// }
// export function TimeInput({
//   onChange = doNothing,
//   disabled = false,
//   placeholder = "Vælg tid",
//   className,
//   value,
//   ...props
// }: TimeInputProps) {
//   return (
//     <input
//       type="time"
//       onChange={(e) => onChange(e.target.value)}
//       value={value ?? "00:00"}
//       {...props}
//       {...classNames(
//         className,
//         css.ChooseTimeInput,
//         disabled ? css.Disabled : ""
//       )}
//       pattern="[0-9]{2}:[0-9]{2}"
//     />
//   );
// };

// TODO: Internationalization plz
const dayAsString = {
  0: "Søn.",
  1: "Man.",
  2: "Tir.",
  3: "Ons.",
  4: "Tor.",
  5: "Fre.",
  6: "Lør.",
};

type CheckboxState = "open" | "closed" | "incomplete";

const Checkbox: React.FC<{
  state: CheckboxState;
  className?: string;
  onClick: () => void;
}> = ({ onClick, className, state }) => {
  return (
    <div
      {...classNames(
        state === "open"
          ? css.CheckboxOpen
          : state === "closed"
          ? css.CheckboxClosed
          : css.CheckboxDefault,
        css.Checkbox,
        className
      )}
      onClick={onClick}
    >
      {state === "closed" && (
        <IonIcon className={css.CheckboxIcon} icon={closeOutline} />
      )}
    </div>
  );
};

export type HintSeverity = "warning" | "error" | "info";

export type ValidateRowResult = {
  severity: HintSeverity;
  message: React.ReactNode | string | undefined;
};

export type ValidateRow = ([day, openingHours]: [
  Day,
  DailyOpeningHours | undefined
]) => ValidateRowResult | undefined;

export type OpeningHoursInputProps = {
  initialOpeningHours?: WeeklyOpeningHours;
  onChange?: (openingHours: WeeklyOpeningHours) => void;
  className?: string;
  /**
   * Returns a hint as to what's wrong
   */
  validateRow?: ValidateRow;
};

const mergeOpeningHours = (openingHours: WeeklyOpeningHours) => {
  return new WeeklyOpeningHours([
    ...new WeeklyOpeningHours()
      .set(0, undefined)
      .set(1, undefined)
      .set(2, undefined)
      .set(3, undefined)
      .set(4, undefined)
      .set(5, undefined)
      .set(6, undefined),
    ...openingHours,
  ]);
};

export const OpeningHoursInput: React.FC<OpeningHoursInputProps> = ({
  initialOpeningHours = new WeeklyOpeningHours(),
  onChange = doNothing,
  className,
  validateRow = doNothing,
  ...props
}) => {
  const [openingHoursForWeek, setOpeningHours] = useState<WeeklyOpeningHours>(
    mergeOpeningHours(initialOpeningHours)
  );

  useEffect(() => {
    setOpeningHours(mergeOpeningHours(initialOpeningHours));
  }, [initialOpeningHours]);

  const [toast] = useIonToast();

  const handleCheckboxClick = useCallback(
    (day: Day) => {
      const openingHours = openingHoursForWeek.get(day);
      const newOpeningHours = new WeeklyOpeningHours([
        ...openingHoursForWeek,
        [
          day,
          openingHours
            ? (() => {
                openingHours.isOpen = !openingHours.isOpen;
                return openingHours;
              })()
            : ({
                from: TimeOfDay.parse("00:00"),
                to: TimeOfDay.parse("23:59"),
                isOpen: true,
              } as DailyOpeningHours),
        ],
      ]);
      setOpeningHours(newOpeningHours);
      onChange(newOpeningHours);
    },
    [openingHoursForWeek]
  );

  // TODO: this is smelling might bad. Make a more generalized implementation for handleFromChange and handleToChange
  const handleFromChange = useCallback(
    (val: string, day: Day) => {
      const openingHours = openingHoursForWeek.get(day);
      let timeOfDay: TimeOfDay;
      try {
        timeOfDay = TimeOfDay.parse(val);
      } catch {
        const date = new Date(val);
        timeOfDay = new TimeOfDay(date.getHours(), date.getMinutes());
      }

      const newOpeningHours = new WeeklyOpeningHours([
        ...openingHoursForWeek,
        [
          day,
          {
            from: timeOfDay,
            to: openingHours ? openingHours.to : TimeOfDay.parse("23:59"),
            isOpen: true,
          } as DailyOpeningHours,
        ],
      ]);
      setOpeningHours(newOpeningHours);
      onChange(newOpeningHours);
    },
    [openingHoursForWeek, onChange]
  );

  const handleToChange = useCallback(
    (val: string, day: Day) => {
      const openingHours = openingHoursForWeek.get(day);

      let timeOfDay: TimeOfDay;
      try {
        timeOfDay = TimeOfDay.parse(val);
      } catch {
        const date = new Date(val);
        timeOfDay = new TimeOfDay(date.getHours(), date.getMinutes());
      }

      const newOpeningHours = new WeeklyOpeningHours([
        ...openingHoursForWeek,
        [
          day,
          {
            from: openingHours ? openingHours.from : TimeOfDay.parse("00:00"),
            to: timeOfDay,
            isOpen: true,
          } as DailyOpeningHours,
        ],
      ]);
      setOpeningHours(newOpeningHours);
      onChange(newOpeningHours);
    },
    [openingHoursForWeek, onChange]
  );

  return (
    <div {...classNames(css.OpeningHoursContainer, className)} {...props}>
      <div>Dag</div>
      <div>Fra kl.</div>
      <div>Til kl.</div>
      <div>Åbent</div>

      {[...openingHoursForWeek].map(
        (
          [day, openingHours]: [
            day: Day,
            openingHours: DailyOpeningHours | undefined
          ],
          i
        ) => {
          const from = openingHours?.from?.toString();
          const to = openingHours?.to?.toString();

          const chooseTimeProps = {
            disabled: !openingHours || !openingHours?.isOpen,
            placeholder: openingHours?.isOpen ? "Vælg tid" : "Lukket",
          };

          const hint = validateRow([day, openingHours]);

          const classNamePrefix =
            day === 0
              ? "sunday"
              : day === 1
              ? "monday"
              : day === 2
              ? "tuesday"
              : day === 3
              ? "wednesday"
              : day === 4
              ? "thursday"
              : day === 5
              ? "friday"
              : "saturday";

          return (
            <>
              <div
                style={{
                  textTransform: "capitalize",
                  fontWeight: "bold",
                  color: openingHours?.isOpen
                    ? "var(--gm-color-sensible-white)"
                    : "var(--gm-color-alt-bg)",
                }}
              >
                {dayAsString[day as WeekDay]}
              </div>
              <TimeInput
                {...chooseTimeProps}
                className={`${classNamePrefix}-from`}
                value={
                  openingHours?.isOpen && from
                    ? TimeOfDay.parse(from)
                    : undefined
                }
                onChange={(val) => handleFromChange(val.toString(), day)}
              />
              <TimeInput
                {...chooseTimeProps}
                className={`${classNamePrefix}-to`}
                value={
                  openingHours?.isOpen && to ? TimeOfDay.parse(to) : undefined
                }
                onChange={(val) => handleToChange(val.toString(), day)}
              />
              <Checkbox
                className={`${classNamePrefix}-open`}
                state={openingHours && openingHours.isOpen ? "open" : "closed"}
                onClick={() => handleCheckboxClick(day)}
              />
              {hint ? (
                <div
                  style={{
                    marginTop: "-8px",
                    gridColumnStart: 2,
                    gridColumnEnd: 5,
                    textAlign: "left",
                    fontSize: "0.8rem",
                    opacity: 0.7,
                    fontWeight: "bold",
                    color:
                      hint.severity === "info"
                        ? "var(--gm-color-sensible-white)"
                        : hint.severity === "warning"
                        ? "var(--gm-color-warning)"
                        : hint.severity === "error"
                        ? "var(--gm-color-error)"
                        : "",
                  }}
                >
                  {hint.message}
                </div>
              ) : null}
            </>
          );
        }
      )}
    </div>
  );
};
